import { useCallback, useMemo, useState } from 'react'
import Cards, { Focused, CallbackArgument } from 'react-credit-cards-2'
import 'react-credit-cards-2/dist/es/styles-compiled.css'
import { useTranslation } from 'react-i18next'

import { FormHandles } from '@unform/core'
import cardValidations from 'card-validator'

import { FormGroup, Input, InputMask, Label } from '~/components/unform'
import { handleValidationsErrors } from '~/helpers'
import useQueryParams from '~/hooks/useQuery'

import InputCreditCard from '../unform/Input/InputCreditCard'
import { getCardNumberMask, getCardRuleByBrand } from './helpers'
import { FormCard } from './validations'

import * as S from './styles'

const libExpectedFields: LibExpectedFields<Focused> = {
  card_name: 'name',
  card_number: 'number',
  card_expiration: 'expiry',
  card_cvv: 'cvc'
}

export const CreditCardForm = ({
  onChange,
  formRef
}: CreditCardFormProps<FormHandles>) => {
  const { t } = useTranslation()

  const debugMode = useQueryParams().get('debug')
  const hidecvv = useQueryParams().get('hidecvv')

  const [callbackData, setCallbackData] = useState<CallbackArgument>()

  const [focus, setFocus] = useState<Focused>('')
  const [card, setCard] = useState<CreditCardData>({
    card_cvv: '',
    card_expiration: '',
    card_name: '',
    card_number: ''
  })

  /**
   * Handle card rules
   * get issuer from callback data and return card rules
   */
  const cardRules = useMemo(() => {
    const issuer = callbackData?.issuer
    const rules = getCardRuleByBrand(issuer)
    const mask = getCardNumberMask(issuer)

    return { ...rules, mask }
  }, [callbackData])

  /**
   * Handle focus in card inputs
   */

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const name = e.target.name as FormCardFields

    const key = libExpectedFields?.[name]

    if (!key) return

    setFocus(key as Focused)
  }

  /**
   * Handle validate card
   */
  const handleValidateCard = useCallback(
    (data: CreditCardData) => {
      try {
        FormCard.validateSync(data, { abortEarly: false })

        const {
          card: { type: brand }
        } = cardValidations.number(data.card_number)

        onChange({ isValid: true, brand, cardData: data })

        formRef.current?.setErrors({})
      } catch (err) {
        console.debug('🚀 ~ validate card ~ err:', err)

        handleValidationsErrors(err, formRef, false, !!debugMode)

        onChange({ isValid: false, brand: '', cardData: data })
      }
    },
    [onChange, formRef, debugMode]
  )

  /**
   * handle changes in card inputs
   */
  const handleChangeCardInputs = useCallback(
    (e: HandleChangeCardInputsEvents) => {
      const { name, value } =
        'target' in e ? e.target : { name: e.name, value: e.value }

      const key = name as FormCardFields

      if (!key) return

      const updatedCard: CreditCardData = { ...card, [key]: value }

      try {
        handleValidateCard(updatedCard)

        setCard(updatedCard)
      } catch (err) {
        console.debug('🚀 ~ handleChangeCardInputs ~ err:', { ...err })
      }
    },
    [card, handleValidateCard]
  )

  return (
    <S.CardForm>
      <fieldset>
        <FormGroup>
          <Label>
            {t('c:components.CreditCardForm.labels.cardholderName')}
          </Label>
          <Input
            name="card_name"
            onChange={handleChangeCardInputs}
            onBlur={handleChangeCardInputs}
            onFocus={handleInputFocus}
            placeholder={t(
              'c:components.CreditCardForm.placeholders.cardholderName'
            )}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>{t('c:components.CreditCardForm.labels.cardNumber')}</Label>
          <InputCreditCard
            name="card_number"
            format={cardRules?.mask}
            maxLength={cardRules?.numberLength}
            mask="•"
            placeholder={t(
              'c:components.CreditCardForm.placeholders.cardNumber'
            )}
            onFocus={handleInputFocus}
            onChange={handleChangeCardInputs}
            onBlur={handleChangeCardInputs}
            required
          />
        </FormGroup>

        <S.CardDateAndCVVWrapper>
          <FormGroup>
            <Label>{t('c:components.CreditCardForm.labels.expiration')}</Label>
            <InputMask
              name="card_expiration"
              format="##/##"
              placeholder={t(
                'c:components.CreditCardForm.placeholders.expiration'
              )}
              mask="•"
              onFocus={handleInputFocus}
              onChange={handleChangeCardInputs}
              onBlur={handleChangeCardInputs}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('c:components.CreditCardForm.labels.cvv')}</Label>
            <InputMask
              name="card_cvv"
              format={'#'.repeat(cardRules?.cvvLength)}
              placeholder={t('c:components.CreditCardForm.placeholders.cvv')}
              mask="•"
              onFocus={handleInputFocus}
              onChange={handleChangeCardInputs}
              onBlur={handleChangeCardInputs}
              required
              style={{
                filter: hidecvv ? 'blur(5px)' : 'none'
              }}
            />
          </FormGroup>
        </S.CardDateAndCVVWrapper>
      </fieldset>

      <aside>
        <Cards
          callback={setCallbackData}
          cvc={hidecvv ? '***' : card.card_cvv}
          expiry={card.card_expiration}
          focused={focus}
          name={card.card_name}
          number={card.card_number}
          locale={{
            valid: t('c:components.CreditCardForm.cardPreview.validUntil')
          }}
          placeholders={{
            name: t('c:components.CreditCardForm.placeholders.cardholderName')
          }}
        />
      </aside>
    </S.CardForm>
  )
}
